import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportFilters } from '@/pages/reports/report-filters-context.js';

type DiscountReport = {
  totalDiscounted: string;
  byPreset: Array<{
    presetId: string | null;
    name: string;
    count: number;
    total: string;
  }>;
  byEmployee: Array<{
    userId: string | null;
    name: string;
    count: number;
    total: string;
    avgPercent: number;
  }>;
  customCount: number;
  presetCount: number;
  customTotal: string;
  presetTotal: string;
  approvalRate: number | null;
};

function qString(q: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

function getDiscountReport(q: {
  period?: string;
  dateFrom?: string;
  dateTo?: string;
  storeId?: string;
}) {
  return apiGet<DiscountReport>(`/api/v1/reports/discounts${qString(q)}`).then((r) => r.data);
}

export function DiscountReportPage() {
  const f = useReportFilters();
  const reportQuery = useQuery({
    queryKey: ['discount-report', f.period, f.storeId, f.dateFrom, f.dateTo],
    queryFn: () =>
      getDiscountReport({
        period: f.period,
        dateFrom: f.dateFrom || undefined,
        dateTo: f.dateTo || undefined,
        storeId: f.storeId || undefined,
      }),
  });

  const data = reportQuery.data;
  const byPreset = useMemo(() => data?.byPreset ?? [], [data]);
  const byEmployee = useMemo(() => data?.byEmployee ?? [], [data]);

  if (reportQuery.isLoading) {
    return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading…</div>;
  }
  if (reportQuery.isError || !data) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load discount report.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-950">Discounts</h2>
        <p className="mt-1 text-sm text-slate-500">
          Preset vs custom, employee breakdown, and approval rates.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Total discounted</p>
          <p className="mt-1 text-3xl font-black text-slate-950">
            {formatCurrency(data.totalDiscounted)}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Preset / custom</p>
          <p className="mt-1 text-lg font-bold text-slate-950">
            {data.presetCount} / {data.customCount}
          </p>
          <p className="text-xs text-slate-500">
            {formatCurrency(data.presetTotal)} · {formatCurrency(data.customTotal)}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm font-medium text-slate-500">Approval rate</p>
          <p className="mt-1 text-3xl font-black text-primary">
            {data.approvalRate != null ? `${data.approvalRate}%` : '—'}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-slate-950">By preset</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-1">Name</th>
                <th className="py-1">Count</th>
                <th className="py-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {byPreset.map((row) => (
                <tr key={row.presetId ?? row.name} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{row.name}</td>
                  <td className="py-2">{row.count}</td>
                  <td className="py-2 text-right">{formatCurrency(row.total)}</td>
                </tr>
              ))}
              {byPreset.length === 0 ? (
                <tr>
                  <td colSpan={3} className="py-6 text-center text-slate-500">
                    No discounts in this period
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-slate-950">By employee</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500">
                <th className="py-1">Name</th>
                <th className="py-1">Count</th>
                <th className="py-1">Avg %</th>
                <th className="py-1 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {byEmployee.map((row) => (
                <tr key={row.userId ?? row.name} className="border-t border-slate-100">
                  <td className="py-2 font-medium">{row.name}</td>
                  <td className="py-2">{row.count}</td>
                  <td className="py-2">{row.avgPercent}%</td>
                  <td className="py-2 text-right">{formatCurrency(row.total)}</td>
                </tr>
              ))}
              {byEmployee.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-slate-500">
                    No discounts in this period
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
